import { BlankSlate } from "@msuite/ui/blank-slate";
import { type PropsWithChildren, useCallback, useMemo, useState } from "react";
import { CapacityCalendarHeader } from "./capacity-calendar-header";
import { CapacityCalendarContext } from "./context";
import { useCapacityCalendar } from "./use-capacity-calendar";

/** Props Interface */
interface CapacityCalendarProps extends PropsWithChildren {
	isLoading?: boolean;
	isEmpty?: boolean;
}

/**
 * The board every capacity view is drawn in: the three-week window, the week and day header, the
 * scroll region and the expansion state of its rows. A view supplies the rows and its own query
 * state and nothing else, so the views stand on one grid and one window and differ only in what a
 * row is.
 */
export function CapacityCalendar({
	children,
	isLoading = false,
	isEmpty = false,
}: CapacityCalendarProps) {
	/** Hooks */
	const controller = useCapacityCalendar();

	/** State */
	// Expansion is keyed on a row id the view chooses, so a view with no second level simply never
	// asks — nothing here knows what kind of entity the id belongs to.
	const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

	/** Functions */
	const toggleExpanded = useCallback((rowId: string) => {
		setExpandedRowIds((previous) =>
			previous.includes(rowId) ? previous.filter((id) => id !== rowId) : [...previous, rowId],
		);
	}, []);

	const getIsExpanded = useCallback(
		(rowId: string) => expandedRowIds.includes(rowId),
		[expandedRowIds],
	);

	/** Computed */
	const contextValue = useMemo(
		() => ({ controller, getIsExpanded, toggleExpanded }),
		[controller, getIsExpanded, toggleExpanded],
	);

	/** Render */
	// The header sits inside the scroll region and sticks to its top. The day columns are `1fr`, so
	// the header and the rows line up only while both are laid out against one width — and a
	// scrollbar that takes space narrows the region it scrolls, never a box standing above it.
	return (
		<CapacityCalendarContext.Provider value={contextValue}>
			<div className="flex-1 min-h-0 card v-stack gap-0 p-0 overflow-hidden">
				<div className="flex-1 min-h-0 overflow-auto v-stack gap-0">
					<div className="sticky top-0 z-2 card-bg">
						<CapacityCalendarHeader />
					</div>
					{/* The rows' own box, growing into what the header leaves: a blank slate fills the
					    board without adding the header's height to it, and the rows stay in block flow
					    rather than becoming flex items that shrink below their content. */}
					<div className="flex-1">
						{isLoading && <BlankSlate isLoading />}
						{!isLoading && isEmpty && <BlankSlate />}
						{children}
					</div>
				</div>
			</div>
		</CapacityCalendarContext.Provider>
	);
}
